import PropTypes from 'prop-types';
import { trans, getPagePath } from '@spotahome/soyuz/client';

import { Button, Link } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import { useSectionNavigation } from '@spotahome/ui-library/src/tenant/context/SectionNavigationContext';

import HomecardsList from './HomecardsList';

import './City.scss';

const MIN_HOMECARDS_TO_SHOW = 4;
const MIN_PROPERTIES_TO_SHOW_LINK = 10;

const City = ({ city }) => {
  const cityName = city.name;
  const cityImagePath = `https://static.spotahome.com/seo-country-pages/${city.countryId}/${city.id}.webp`;

  const { scrollToSection } = useSectionNavigation();

  const baseSearchPath = getPagePath('marketplace.free-search', {
    params: { locationString: city.id }
  });

  const scrollToTopCities = () => {
    scrollToSection('top-cities');
  };

  const getPropertyTypePath = propertyType => {
    return `${baseSearchPath}/for-rent:${propertyType}`;
  };

  const getPropertyTypeLabel = propertyType => {
    const propertyTypeLabel = trans(
      `search.filter_header.results.${propertyType}`
    );

    return trans('seo.country.view.more.in.city', {
      propertyType: propertyTypeLabel.toLowerCase(),
      city: cityName
    });
  };

  const propertyTypeButtons = [
    { type: 'studios', count: city.studios, iconName: 'bed' },
    { type: 'rooms', count: city.rooms, iconName: 'door_open' },
    { type: 'apartments', count: city.apartments, iconName: 'home' }
  ].filter(btn => btn.count > MIN_PROPERTIES_TO_SHOW_LINK);

  const homecards = city.homecards || [];

  return (
    <div className="city">
      <section className="city__info">
        <div className="city__info__content">
          <div className="city__info__image-container">
            <img
              src={cityImagePath}
              alt={cityName}
              className="city__info__image"
              loading="lazy"
            />
          </div>
          <div className="city__info__text">
            <h2 className="city__info__title">{cityName}</h2>

            {city.subtitle && (
              <h3 className="city__info__subtitle">
                {trans(`seo.countries.${city.countryId}.${city.id}.subtitle`)}
              </h3>
            )}

            <div
              className="city__info__description"
              dangerouslySetInnerHTML={{
                __html: trans(
                  `seo.countries.${city.countryId}.${city.id}.description`
                )
              }}
            />
            <div className="city__info__actions">
              {propertyTypeButtons.map(({ type, iconName }) => (
                <Button
                  key={type}
                  href={getPropertyTypePath(type)}
                  color="tertiary"
                  size="medium"
                  className="city__info__action-button"
                  renderIcon={() => <DSIcon name={iconName} size="m" />}
                >
                  {getPropertyTypeLabel(type)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="city__homecards">
        {homecards.length >= MIN_HOMECARDS_TO_SHOW && (
          <>
            <h3 className="city__homecards__title">
              {trans('seo.country.our.properties.in.city', { city: cityName })}
            </h3>
            <HomecardsList homecards={homecards} />
          </>
        )}

        <div className="city__homecards__footer">
          <Button
            href={baseSearchPath}
            color="primary"
            size="large"
            className="city__view-all-button"
          >
            {trans('seo.country.view.all.in.city', { city: cityName })}
          </Button>
          <Link className="city__back-link" onClick={scrollToTopCities}>
            {trans('seo.country.back.all.city')}
          </Link>
        </div>
      </section>
    </div>
  );
};

City.propTypes = {
  city: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    countryId: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    description: PropTypes.arrayOf(PropTypes.string),
    homecards: PropTypes.arrayOf(PropTypes.shape({})),
    rooms: PropTypes.number,
    apartments: PropTypes.number,
    studios: PropTypes.number
  }).isRequired
};

export default City;
